import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  fetchStore,
  fetchStoreSucceeded,
  reset,
  selectCart,
  selectError,
  selectIsLoading,
  selectProductSections,
  selectStore,
  selectStoreCategories
} from "../../redux/store/storeSlice";
import {fetchCart} from "../../redux/cart/cartSlice";
import {StoreModel} from "../../models/store";
import {Container} from "../containers/Container";
import {StoreHeaderView} from "./StoreHeaderView";
import {StoreSectionView} from "./StoreSectionView";
import {StoreCategorySectionView} from "./StoreCategorySectionView";
import {CurrencyUtils} from "../../utils/currencyUtils";
import {CartButton} from "../buttons/CartButton";
import {setAddedCartItem} from "../../redux/navigation/navigationSlice";
import {ProductCategoryModel} from "../../models/productCategory";
import {StoreSectionModel} from "../../models/storeSection";
import {ProductModel} from "../../models/product";
import {ProductSkuModel} from "../../models/productSku";
import {StoreEmptyStateView} from "../storeFeed/StoreEmptyStateView";
import {WarningColorIcon} from "../icons";
import {useRouter} from "next/router";
import {useContextualRouting} from "next-use-contextual-routing";
import {useModalRouting} from "../../utils/useModalRouting";
import {NavBarNegativeHeight} from "../navBars/NavBarMain";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";
import Link from 'next/link'

export interface StoreViewProps {
  storeId: string
  store?: StoreModel
}

export const StoreView: React.FC<StoreViewProps> = ({ storeId, store: preloadedStore }) => {
  const { t } = useTranslation()

  const router = useRouter()

  const dispatch = useAppDispatch();
  const store = useAppSelector(selectStore)
  let productSections = useAppSelector(selectProductSections)
  let storeCategories = useAppSelector(selectStoreCategories)
  const cart = useAppSelector(selectCart)
  const isLoading = useAppSelector(selectIsLoading)
  const error = useAppSelector(selectError)
  const [loading, setLoading] = useState(isLoading)

  if (loading && storeCategories.length <= 0) {
    storeCategories = [[1, 2, 3].map(i => {
      return ProductCategoryModel.fromJson({ _id: i.toString() })!
    })]
  }

  if (loading && productSections.length <= 0) {
    productSections = [1, 2, 3].map(i => {
      const products = Array.from({ length: 10 }, (_, i) => i + 1).map((i) => ProductModel.fromJson({ _id: i.toString(), name: 'Default', skus: [ProductSkuModel.fromJson({ price: 0 })!] })!)
      const section = StoreSectionModel.fromJson({ _id: i.toString() })!
      section.products = products
      return section
    })
  }

  const cartTotal = CurrencyUtils.stringFromPrice(cart?.totalPrice, cart?.currencyCode)
  const cartTitle = t('store:store_button_view_cart')
  const cartQuantity = cart?.items.reduce((sum, item) => {
    return sum + (item.quantity ?? 0)
  }, 0).toString() ?? "0"

  const timeout = useRef<number>();
  const [cartToolbarHidden, setCartToolbarHidden] = useState(true)
  const { createRoute } = useModalRouting();

  useEffect(() => {
    const cartIsEmpty = !cart || cart.items.length <= 0

    timeout.current = window.setTimeout(() => {
      setCartToolbarHidden(cartIsEmpty)
    }, 200);

    return () => {
      window.clearTimeout(timeout.current)
    }
  }, [cart])

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Store' }))

    dispatch(setAddedCartItem())
    dispatch(reset())
    if (preloadedStore) {
      dispatch(fetchStoreSucceeded(preloadedStore))
    }
    if (storeId) {
      dispatch(fetchStore(storeId))
      dispatch(fetchCart(storeId))
    }
  }, [])

  useEffect(() => {
    setLoading(!store || isLoading)
  }, [store, isLoading])

  if (error) {
    return <div className={`h-screen flex flex-col justify-center items-center ${NavBarNegativeHeight}`}>
      <StoreEmptyStateView
        onClick={() => {
          if (storeId) {
            dispatch(fetchStore(storeId))
            dispatch(fetchCart(storeId))
          }
        }}
        isLoading={isLoading}
        icon={<WarningColorIcon />}
        title={error.localizedTitle}
        subtitle={error.localizedMessage}
        buttonTitle={t('store:store_empty_state_button_try_again')}
      />
    </div>
  }

  return (
    <div>
      <Container>
        <StoreHeaderView loading={loading} storeHeader={store?.header} />

        <div className="flex flex-col space-y-8 md:space-y-10 lg:space-y-12 w-full">
          {storeCategories.map((categories, i) => {
            return <StoreCategorySectionView loading={loading} categories={categories} key={i.toString()} />
          })}
        </div>

        <div className="mt-8 mb-24 md:mt-10 lg:mt-12 flex flex-col space-y-8 md:space-y-10 lg:space-y-12 w-full">
          {productSections.map(section => {
            return <StoreSectionView loading={loading} storeSection={section} key={section.id} carouselType={productSections.length <= 2 ? 'grid' : 'row'} />
          })}
        </div>

      </Container>

      <div className={`fixed z-20 w-full bottom-6 lg:hidden transition-all transform ease-in-out duration-500 px-4 sm:px-8 lg:px-10 max-w-screen-2xl ${cartToolbarHidden ? 'opacity-0 translate-y-12' : 'opacity-100 translate-y-0'}`}>
        <div className="mx-2 md:mx-4 lg:mx-6 rounded-full shadow-lg">
          <Link
            href={createRoute({ name: 'cart', query: { cart_id: store?.id } })}
            as={`/cart/${store?.id}`}
            shallow={true}
            passHref
          >
           <a>
             <CartButton
               total={cartTotal}
               buttonTitle={cartTitle}
               quantity={cartQuantity}
             />
           </a>
          </Link>
        </div>
      </div>
    </div>
  )
}